/* import __COLOCATED_TEMPLATE__ from './card.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type ContentSection from 'embercom/models/content-service/content-section';
import {
  humanReadableObjectNames,
  objectIcons,
  objectNames,
} from 'embercom/models/data/matching-system/matching-constants';
import type IntlService from 'ember-intl/services/intl';
import { capitalize } from '@ember/string';
import Admin from 'embercom/models/admin';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type SuggestionService from 'embercom/services/suggestion-service';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type ContentStatistic from 'embercom/models/outbound/content-statistic';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import { EntityType } from 'embercom/models/data/entity-types';

interface Args {
  section: ContentSection;
  relationship: ContentSectionRelationship;
  onDeleteRequested?: (section: ContentSection, contentTypeName: string) => void;
  updateCardState?: (section: ContentSection, deleted: boolean) => void;
  getAiStatsForSection: (section: ContentSection) => ContentStatistic[];
  loadingAiStats: boolean;
  selector: 'source' | 'target';
}

export default class SideBySideReviewCardComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare suggestionService: SuggestionService;

  @tracked isDeleted = false;

  get section() {
    return this.args.section;
  }

  get content() {
    return this.section.relatedContent;
  }

  get isContentEditable() {
    return this.content?.isEditable ?? false;
  }

  get isExternalContent() {
    return this.section.entityType === EntityType.ExternalContent;
  }

  get editLabel() {
    if (this.isContentEditable) {
      return this.intl.t('knowledge-hub.content-editor.side-by-side-review.card.edit');
    }

    return this.intl.t(
      'knowledge-hub.content-editor.side-by-side-review.card.view-external-content',
    );
  }

  get updatedAt() {
    if (!this.content) {
      return '';
    }
    return this.content.updatedAt;
  }

  get icon(): InterfaceIconName {
    let entityType = this.section.entityType;
    if (!entityType) {
      return 'note' as InterfaceIconName;
    }

    return objectIcons[this.section.entityType];
  }

  get shouldShowDeleteButton() {
    if (this.section.entityType === EntityType.ExternalContent) {
      return false;
    }

    return true;
  }

  get contentTypeName() {
    return capitalize(humanReadableObjectNames[this.section.entityType]);
  }

  get lowercaseContentTypeName() {
    return this.contentTypeName.toLowerCase();
  }

  get lastUpdatedBy() {
    let adminId = this.section.relatedContent?.updaterId;

    if (!adminId) {
      return undefined;
    }

    return Admin.peekAndMaybeLoad(this.store, adminId.toString());
  }

  get isOutdated() {
    return this.section.isOutdated;
  }

  get aiStats(): ContentStatistic[] {
    return this.args.getAiStatsForSection(this.section);
  }

  @action viewContent() {
    if (!this.content) {
      return;
    }

    if (this.content.isEditable) {
      this.knowledgeHubDrawerEditorService.openEditDrawer({
        activeContentId: this.section.entityId,
        activeContentType: objectNames[this.section.entityType],
        queryParams: {
          fromConDupeSuggestion: this.args.relationship.id, // pass through the relationship ID so we can navigate back to it
        },
      });
    } else {
      this.knowledgeHubDrawerEditorService.openViewDrawer({
        activeContentId: this.section.entityId,
        activeContentType: objectNames[this.section.entityType],
        routeName: 'apps.app.knowledge-hub.all-content',
        newTab: true,
      });
    }
  }

  @action
  requestDelete() {
    this.isDeleted = true;
    this.args.updateCardState?.(this.section, true);
  }

  @action
  requestUndo() {
    this.isDeleted = false;
    this.args.updateCardState?.(this.section, false);
  }

  get cleanedHTML(): string {
    let html = this.section.html || '';

    // Check if there's an h1 tag at the start
    let removeTitleRegex = /^(\s*)<h1[^>]*>.*?<\/h1>/i;
    let match = html.match(removeTitleRegex);
    if (match) {
      // Remove the h1 tag
      html = html.replace(removeTitleRegex, '');
      // Remove any newlines following the h1 tag
      html = html.replace(/^(\s*\n)+/, '');
    }

    return html;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::SideBySideReview::Card': typeof SideBySideReviewCardComponent;
    'knowledge-hub/content-editor/side-by-side-review/card': typeof SideBySideReviewCardComponent;
  }
}
